import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { expertDashboardApi, GoogleCalendar } from '../../../lib/http';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (calendarId: string) => void;
  isSelecting?: boolean;
}

const CalendarPickerModal: React.FC<Props> = ({ isOpen, onClose, onSelect, isSelecting }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['google-calendars'],
    queryFn: async () => {
      const response = await expertDashboardApi.integrations.listGoogleCalendars();
      return response.data;
    },
    enabled: isOpen,
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Select Calendar
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Choose which Google Calendar to sync your bookings with.
          </p>

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
              Failed to load calendars. Please try again.
            </div>
          )}

          {/* Calendars List */}
          {!isLoading && !error && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data?.calendars?.map((calendar: GoogleCalendar) => (
                <button
                  key={calendar.id}
                  onClick={() => onSelect(calendar.id)}
                  disabled={isSelecting}
                  className="w-full p-4 text-left rounded-xl border border-gray-200 hover:border-pink-300 hover:bg-pink-50 transition-colors flex items-center gap-3 disabled:opacity-50"
                >
                  <div
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: calendar.background_color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      <span className="truncate">{calendar.summary}</span>
                      {calendar.primary && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded flex-shrink-0">
                          Primary
                        </span>
                      )}
                    </div>
                    {calendar.description && (
                      <div className="text-sm text-gray-500 truncate">
                        {calendar.description}
                      </div>
                    )}
                  </div>
                </button>
              ))}

              {data?.calendars?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No calendars found with write access.
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPickerModal;
